import { BaseSupabaseService } from './BaseSupabaseService';
import { PhoneNumber } from '../types';
import { handleServiceError } from '../utils/errorHandler';
import { requestEdgeFunction } from './edgeFunctionClient';

class PhoneServiceClass extends BaseSupabaseService<PhoneNumber> {
  constructor() {
    super('phone_numbers');
  }

  async findByStudentId(studentId: number): Promise<PhoneNumber | null> {
    const results = await this.findAll({
      filters: [{ column: 'student_id', value: studentId }],
    });
    return results[0] || null;
  }

  async findByStudentIds(studentIds: number[]): Promise<PhoneNumber[]> {
    if (studentIds.length === 0) return [];
    try {
      const response = await requestEdgeFunction<{ data: PhoneNumber[] }>('students', {
        method: 'POST',
        body: {
          action: 'list-student-phones',
          payload: { studentIds },
        },
      });
      return response.data ?? [];
    } catch (error) {
      handleServiceError(error, 'buscar telefones');
      return [];
    }
  }

  async upsertForStudent(studentId: number, data: Partial<PhoneNumber>): Promise<PhoneNumber> {
    const existing = await this.findByStudentId(studentId);
    const payload: Partial<PhoneNumber> = {
      country_code: data.country_code,
      phone_number: data.phone_number || null,
      ...(data.country_iso != null ? { country_iso: data.country_iso } : {}),
    };

    if (existing) {
      return this.update(existing.id, payload);
    }
    return this.create({ ...payload, student_id: studentId });
  }

  async checkPhoneExists(
    countryCode: string,
    phoneNumber: string,
    excludeStudentId?: number
  ): Promise<boolean> {
    if (!phoneNumber) return false;
    const results = await this.findAll({
      filters: [
        { column: 'country_code', value: countryCode },
        { column: 'phone_number', value: phoneNumber },
      ],
    });
    if (excludeStudentId) {
      return results.some((p) => p.student_id !== excludeStudentId);
    }
    return results.length > 0;
  }
}

export const PhoneService = new PhoneServiceClass();
